export enum DayOfWeek {
	Sunday,
	Monday,
	Tuesday,
	Wednesday,
	Thursday,
	Friday,
	Saturday
}
enum DatePart {
	Year = 0,
	DayOfYear = 1,
	Month = 2,
	Day = 3
}

export class DateTime {

	static readonly TicksPerMinute = 60;
	static readonly TicksPerHour = 60 * DateTime.TicksPerMinute;
	static readonly TicksPerDay = DateTime.TicksPerHour * 24;

	// Number of days in a non-leap year
	private static readonly DaysPerYear = 365;
	// Number of days in 4 years
	private static readonly DaysPer4Years = DateTime.DaysPerYear * 4 + 1;       // 1461
	// Number of days in 100 years
	private static readonly DaysPer100Years = DateTime.DaysPer4Years * 25 - 1;  // 36524
	// Number of days in 400 years
	private static readonly DaysPer400Years = DateTime.DaysPer100Years * 4 + 1; // 146097
	private static readonly DaysToMonth365: number[] = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 365];
	private static readonly DaysToMonth366: number[] = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335, 366];

	static readonly TicksToUnix = 62135596800;

	static readonly UnixEpoch: DateTime = new DateTime(DateTime.DateToTicks(1970, 1, 1));
	static readonly UnixEpochMon: DateTime = new DateTime(DateTime.DateToTicks(1970, 1, 5));
	private ticks: number;


	constructor(seconds: number) {
		this.ticks = seconds;

	}
	Deduct(at: DateTime): DateTime {
		const diff = this.ticks - at.ticks;
		return new DateTime(diff);
	}
	get Ticks() {
		return this.ticks;
	}
	get Text() {
		return `${this.Year}/${this.Month}/${this.Day} ${this.Hour}:${this.Minute}`;
	}
	get DayOfWeek(): DayOfWeek {
		return (Math.floor((this.ticks + DateTime.TicksToUnix) / DateTime.TicksPerDay) + 1) % 7;
	}
	get DayOfYear() {
		return this.getDatePart(DatePart.DayOfYear);
	}
	get TimeOfDay(): number {
		return (this.ticks % DateTime.TicksPerDay);
	}
	get Minute(): number {
		return Math.floor(this.ticks / DateTime.TicksPerMinute) % 60;
	}
	get Hour(): number {
		return Math.floor(this.ticks / DateTime.TicksPerHour) % 24;
	}
	get Year(): number {
		return this.getDatePart(DatePart.Year);
	}
	get Month(): number {
		return this.getDatePart(DatePart.Month);
	}
	get Day(): number {
		return this.getDatePart(DatePart.Day);
	}
	get Date(): DateTime {
		return new DateTime(DateTime.DateToTicks(this.Year, this.Month, this.Day));
	}
	get TotalMinutes(): number {
		return this.ticks / DateTime.TicksPerMinute;
	}
	get TotalDays(): number {
		return this.ticks / DateTime.TicksPerDay;
	}

	GetYearDays(): number {
		const year = this.Year;
		return ((year % 4 === 0 && year % 100 > 0) || year % 400 == 0) ? 366 : 365;
	}

	AddDays(days: number): DateTime {
		return new DateTime(this.ticks + days * DateTime.TicksPerDay);
	}

	AddMonths(months: number): DateTime {
		if (months < -120000 || months > 120000) throw "BadMonths";
		const parts = this.getDateParts();
		let d = parts.day;
		let m = parts.month;
		let y = parts.year;
		let i = m - 1 + months;
		if (i >= 0) {
			m = i % 12 + 1;
			y = y + Math.floor(i / 12);
		}
		else {
			m = 12 + (i + 1) % 12;
			y = y + Math.floor((i - 11) / 12);
		}
		if (y < 1 || y > 9999) {
			throw "DateArithmetic";
		}
		const days = DateTime.DaysInMonth(y, m);
		if (d > days) d = days;
		return new DateTime((DateTime.DateToTicks(y, m, d) + this.ticks % DateTime.TicksPerDay));
	}

	AddYears(years: number): DateTime {
		if (years < -10000 || years > 10000) throw "BadYears";
		return this.AddMonths(years * 12);
	}
	AddHours(hours: number): DateTime {
		return new DateTime(this.ticks + hours * DateTime.TicksPerHour);
	}
	AddMinutes(mins: number): DateTime {
		return new DateTime(this.ticks + mins * DateTime.TicksPerMinute);
	}
	Add(ticks: number): DateTime {
		return new DateTime(this.ticks + ticks);
	}
	toString() {
		return this.Year + '/' + this.Month + '/' + this.Day + ' ' + this.Hour + ':' + this.Minute;
	}

	private static DateToTicks(year: number, month: number, day: number) {
		if (year >= 1 && year <= 9999 && month >= 1 && month <= 12) {
			const days = DateTime.IsLeapYear(year) ? DateTime.DaysToMonth366 : DateTime.DaysToMonth365;
			if (day >= 1 && day <= days[month] - days[month - 1]) {
				const y = (year) - 1;
				let n = y * 365 + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + days[month - 1] + day - 1;
				n *= DateTime.TicksPerDay;
				n -= DateTime.TicksToUnix;
				return n;
			}
		}
		throw "BadYearMonthDay";
	}
	static FromDate(year: number, month: number, day: number) {
		return new DateTime(DateTime.DateToTicks(year, month, day));
	}
	static DaysInMonth(year: number, month: number) {
		if (month < 1 || month > 12) throw "ArgumentOutOfRange_Month";
		// IsLeapYear checks the year argument
		const days = DateTime.IsLeapYear(year) ? DateTime.DaysToMonth366 : DateTime.DaysToMonth365;
		return days[month] - days[month - 1];
	}
	static IsLeapYear(year: number) {
		if (year < 1 || year > 9999) {
			throw "ArgumentOutOfRange_Year";
		}
		return year % 4 == 0 && (year % 100 != 0 || year % 400 == 0);
	}
	static getTZOff(ticks: number, tz: string) {
		const orig = new Date(ticks * 1000);
		const tzt = new Date(orig.toLocaleString("en-US", { timeZone: tz }));
		let tzoff = orig.getTimezoneOffset() * 60;
		tzoff = (orig.getTime() - tzt.getTime()) / 1000 + tzoff;
		return tzoff;
	}
	static convertFromUTC(ticks: number, tz: string) {
		const tzoff = DateTime.getTZOff(ticks, tz);
		return ticks - tzoff;
	}
	static convertToUTC(ticks: number, tz: string) {
		const tzoff = DateTime.getTZOff(ticks, tz);
		return ticks + tzoff;
	}
	static utcNow() {
		var date = new Date();
		const ms = date.getTime() / 1000;// + date.getTimezoneOffset() * 60;
		return Math.round(ms);
	}

	private getDatePart(part: DatePart) {
		let ticks = this.ticks + DateTime.TicksToUnix;
		// n = number of days since 1/1/0001
		let n = Math.floor(ticks / DateTime.TicksPerDay);
		// y400 = number of whole 400-year periods since 1/1/0001
		let y400 = Math.floor(n / DateTime.DaysPer400Years);
		// n = day number within 400-year period
		n -= y400 * DateTime.DaysPer400Years;
		// y100 = number of whole 100-year periods within 400-year period
		let y100 = Math.floor(n / DateTime.DaysPer100Years);
		// Last 100-year period has an extra day, so decrement result if 4
		if (y100 == 4) y100 = 3;
		// n = day number within 100-year period
		n -= y100 * DateTime.DaysPer100Years;
		// y4 = number of whole 4-year periods within 100-year period
		let y4 = Math.floor(n / DateTime.DaysPer4Years);
		// n = day number within 4-year period
		n -= y4 * DateTime.DaysPer4Years;
		// y1 = number of whole years within 4-year period
		let y1 = Math.floor(n / DateTime.DaysPerYear);
		// Last year has an extra day, so decrement result if 4
		if (y1 == 4) y1 = 3;
		// If year was requested, compute and return it
		if (part == DatePart.Year) {
			return y400 * 400 + y100 * 100 + y4 * 4 + y1 + 1;
		}
		// n = day number within year
		n -= y1 * DateTime.DaysPerYear;
		// If day-of-year was requested, return it
		if (part == DatePart.DayOfYear) return n + 1;
		// Leap year calculation looks different from IsLeapYear since y1, y4,
		// and y100 are relative to year 1, not year 0
		const leapYear = y1 == 3 && (y4 != 24 || y100 == 3);
		const days = leapYear ? DateTime.DaysToMonth366 : DateTime.DaysToMonth365;
		// All months have less than 32 days, so n >> 5 is a good conservative
		// estimate for the month
		let m = n >> 5 + 1;
		// m = 1-based month number
		while (n >= days[m]) m++;
		// If month was requested, return it
		if (part == DatePart.Month) return m;
		// Return 1-based day-of-month
		return n - days[m - 1] + 1;
	}

	// Exactly the same as GetDatePart(int part), except computing all of
	// year/month/day rather than just one of them.  Used when all three
	// are needed rather than redoing the computations for each.
	private getDateParts() {

		const ticks = this.ticks + DateTime.TicksToUnix;
		// n = number of days since 1/1/0001
		let n = Math.floor(ticks / DateTime.TicksPerDay);
		// y400 = number of whole 400-year periods since 1/1/0001
		let y400 = Math.floor(n / DateTime.DaysPer400Years);
		// n = day number within 400-year period
		n -= y400 * DateTime.DaysPer400Years;
		// y100 = number of whole 100-year periods within 400-year period
		let y100 = Math.floor(n / DateTime.DaysPer100Years);
		// Last 100-year period has an extra day, so decrement result if 4
		if (y100 == 4) y100 = 3;
		// n = day number within 100-year period
		n -= y100 * DateTime.DaysPer100Years;
		// y4 = number of whole 4-year periods within 100-year period
		let y4 = Math.floor(n / DateTime.DaysPer4Years);
		// n = day number within 4-year period
		n -= y4 * DateTime.DaysPer4Years;
		// y1 = number of whole years within 4-year period
		let y1 = Math.floor(n / DateTime.DaysPerYear);
		// Last year has an extra day, so decrement result if 4
		if (y1 == 4) y1 = 3;
		// compute year
		const year = y400 * 400 + y100 * 100 + y4 * 4 + y1 + 1;
		// n = day number within year
		n -= y1 * DateTime.DaysPerYear;
		// dayOfYear = n + 1;
		// Leap year calculation looks different from IsLeapYear since y1, y4,
		// and y100 are relative to year 1, not year 0
		const leapYear = y1 == 3 && (y4 != 24 || y100 == 3);
		const days = leapYear ? DateTime.DaysToMonth366 : DateTime.DaysToMonth365;
		// All months have less than 32 days, so n >> 5 is a good conservative
		// estimate for the month
		let m = (n >> 5) + 1;
		// m = 1-based month number
		while (n >= days[m]) m++;
		// compute month and day
		const month = m;
		const day = n - days[m - 1] + 1;

		return {
			year: year,
			month: month,
			day: day
		}
	}
}
